import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';
import { useAuth } from '@/stores/authStore';
import { useTranslation } from 'react-i18next';

export default function SignupChoicePage() {
  const navigate = useNavigate();
  const { user, _hasHydrated } = useAuth();
  const { t } = useTranslation(['auth', 'common']);

  if (!_hasHydrated) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  if (user) {
    if (user.role === 'CANDIDATE') navigate('/candidate/dashboard');
    else if (user.role === 'RECRUITER') navigate('/recruiter/dashboard');
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-purple-50/60 fixed inset-0 overflow-auto flex flex-col">
      {/* Glow blobs */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute -top-32 -right-32 w-[32rem] h-[32rem] bg-blue-200 rounded-full blur-3xl opacity-50" />
        <div className="absolute top-1/3 -left-24 w-80 h-80 bg-purple-200 rounded-full blur-3xl opacity-40" />
        <div className="absolute -bottom-24 left-1/3 w-96 h-96 bg-purple-100 rounded-full blur-3xl opacity-40" />
      </div>
      {/* Top bar */}
      <div className="relative w-full max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-blue-600 transition-colors group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
          <span>{t('common:nav.home')}</span>
        </Link>
        <p className="text-sm text-gray-500">
          {t('auth:register.haveAccount')}{' '}
          <Link to="/login" className="text-blue-600 hover:text-blue-700 font-semibold transition-colors">
            {t('auth:register.signInLink')}
          </Link>
        </p>
      </div>

      {/* Main content */}
      <div className="relative flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-4xl">
          {/* Title */}
          <div className="text-center mb-10 animate-slide-down">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3 tracking-tight">
              {t('auth:register.signupChoiceTitle')}
            </h1>
            <p className="text-gray-500 text-base">
              {t('auth:register.signupChoiceSubtitle')}
            </p>
          </div>

          {/* Role Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 animate-slide-up">
            {/* Candidate Card */}
            <Link
              to="/register/candidate"
              className="group bg-white/70 backdrop-blur-sm border border-gray-200 rounded-2xl hover:bg-white hover:border-gray-300 hover:shadow-lg transition-all hover:-translate-y-1 flex flex-col"
            >
              <div className="p-7 flex flex-col flex-1">
                <div className="flex items-start justify-between mb-5">
                  <span className="text-xs font-semibold bg-green-50 text-green-700 border border-green-200 px-2.5 py-1 rounded-full">
                    {t('auth:register.candidateCard.tag')}
                  </span>
                </div>

                <h2 className="text-xl font-bold text-gray-900 mb-1 group-hover:text-blue-700 transition-colors">
                  {t('auth:register.candidateCard.title')}
                </h2>
                <p className="text-sm text-gray-500 mb-6 leading-relaxed">
                  {t('auth:register.candidateCard.subtitle')}
                </p>

                <ul className="space-y-3 mb-8 flex-1">
                  {([1, 2, 3] as const).map((i) => (
                    <li key={i} className="flex items-center gap-2.5 text-sm text-gray-600">
                      <Check className="w-4 h-4 text-blue-500 flex-shrink-0" />
                      {t(`auth:register.candidateCard.feature${i}`)}
                    </li>
                  ))}
                </ul>

                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <span className="text-sm font-semibold text-blue-600 group-hover:text-blue-700 transition-colors">
                    {t('auth:register.candidateCard.cta')}
                  </span>
                  <ArrowRight className="w-4 h-4 text-blue-500 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>

            {/* Recruiter Card */}
            <Link
              to="/register/recruiter"
              className="group bg-white/70 backdrop-blur-sm border border-gray-200 rounded-2xl hover:bg-white hover:border-gray-300 hover:shadow-lg transition-all hover:-translate-y-1 flex flex-col"
            >
              <div className="p-7 flex flex-col flex-1">
                <div className="flex items-start justify-between mb-5">
                  <span className="text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-200 px-2.5 py-1 rounded-full">
                    {t('auth:register.recruiterCard.tag')}
                  </span>
                </div>

                <h2 className="text-xl font-bold text-gray-900 mb-1 group-hover:text-purple-700 transition-colors">
                  {t('auth:register.recruiterCard.title')}
                </h2>
                <p className="text-sm text-gray-500 mb-6 leading-relaxed">
                  {t('auth:register.recruiterCard.subtitle')}
                </p>

                <ul className="space-y-3 mb-8 flex-1">
                  {([1, 2, 3] as const).map((i) => (
                    <li key={i} className="flex items-center gap-2.5 text-sm text-gray-600">
                      <Check className="w-4 h-4 text-purple-500 flex-shrink-0" />
                      {t(`auth:register.recruiterCard.feature${i}`)}
                    </li>
                  ))}
                </ul>

                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <span className="text-sm font-semibold text-purple-600 group-hover:text-purple-700 transition-colors">
                    {t('auth:register.recruiterCard.cta')}
                  </span>
                  <ArrowRight className="w-4 h-4 text-purple-500 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
